/**
 * AASHTO 1993 Constants
 *
 * Centralized constants for AASHTO pavement design calculations.
 * These values are referenced from Excel DATABASE.txt and Design_LCR sheets.
 *
 * References:
 * - AASHTO Guide for Design of Pavement Structures, 1993
 * - Excel DATABASE.txt (H29, H31)
 * - Excel Design_LCR.txt (G85, K85)
 *
 * Usage:
 *   import { UNIT_CONVERSION, DEFAULT_LCR } from '../constants/AASHTOConstants';
 *   const thickness_in = thickness_m / UNIT_CONVERSION.METERS_TO_INCHES;
 *   const lcrBase = base.lcr || DEFAULT_LCR.BASE_EG20S;
 */

/**
 * Unit Conversion Constants
 * 1 meter = 39.3701 inches, therefore 1 inch = 0.0254 meters
 */
export const UNIT_CONVERSION = {
    /**
     * Conversion factor: meters to inches
     * Usage: thickness_in = thickness_m / METERS_TO_INCHES
     * Excel formula: =thickness_m/0.0254
     */
    METERS_TO_INCHES: 0.0254,

    /**
     * Conversion factor: inches to meters
     * Usage: thickness_m = thickness_in * INCHES_TO_METERS
     * Excel formula: =thickness_in*0.0254
     */
    INCHES_TO_METERS: 0.0254,
} as const;

/**
 * Default Layer Contribution Ratio (LCR) Values
 *
 * LCR represents the stabilization benefit provided by geogrid reinforcement.
 * Values are calculated using quadratic regression formulas based on CBR.
 *
 * Excel References:
 * - BASE_EG20S: DATABASE.txt H29, Design_LCR.txt G85
 * - SUBBASE_EG30S: Design_LCR.txt K85
 * - SUBBASE_EG40S: DATABASE.txt H31
 *
 * Note: These are fallback values used when dynamic LCR calculation is not available.
 * Primary LCR values should come from MaterialDatabaseService or backend calculations.
 */
export const DEFAULT_LCR = {
    /**
     * MacGrid EG 20S - Base Layer
     * Excel: DATABASE.txt H29 = 1.54147
     * Excel: Design_LCR.txt G85 = 1.54147
     * Used for: Base course geogrid stabilization
     */
    BASE_EG20S: 1.54147,

    /**
     * MacGrid EG 30S - Subbase Layer
     * Excel: Design_LCR.txt K85 = 1.588617
     * Used for: Subbase course geogrid stabilization (CBR=10)
     * Note: This is the correct default for subbase calculations
     */
    SUBBASE_EG30S: 1.588617,

    /**
     * MacGrid EG 40S - Subbase Layer (Alternative)
     * Excel: DATABASE.txt H31 = 1.67984
     * Used for: Higher-strength subbase geogrid stabilization
     * Note: Used in some legacy calculations
     */
    SUBBASE_EG40S: 1.67984,
} as const;

/**
 * AASHTO Calculation Constants
 * Additional constants used in AASHTO 1993 design methodology
 */
export const AASHTO_CONSTANTS = {
    /**
     * CBR to Resilient Modulus conversion
     * AASHTO 1993 correlation: Mr (psi) = 1500 * CBR
     */
    CBR_TO_MR_COEFFICIENT: 1500,

    /**
     * Minimum layer thicknesses (meters)
     * These are typical minimum values from AASHTO guidelines
     */
    MIN_THICKNESS: {
        SURFACE: 0.1, // 100 mm minimum asphalt layer
        BASE: 0.15, // 150 mm minimum base course
        SUBBASE: 0.15, // 150 mm minimum subbase course
    },
} as const;

/**
 * Geogrid Product Identifiers
 * Centralized product IDs to eliminate string duplication across the codebase
 *
 * Usage:
 *   import { GEOGRID_PRODUCTS } from '../constants/AASHTOConstants';
 *   const productId = GEOGRID_PRODUCTS.MACGRID_EG_30S;
 */
export const GEOGRID_PRODUCTS = {
    /**
     * MacGrid EG 20S - Base layer geogrid
     * Typical CBR range: 5-20%
     */
    MACGRID_EG_20S: 'macgrid_eg_20s',

    /**
     * MacGrid EG 30S - Subbase layer geogrid
     * Typical CBR range: 5-20%
     */
    MACGRID_EG_30S: 'macgrid_eg_30s',

    /**
     * MacGrid EG 40S - High-strength subbase geogrid
     * Typical CBR range: 5-20%
     */
    MACGRID_EG_40S: 'macgrid_eg_40s',

    /**
     * Multicomposito - Composite drainage product
     * Combines geogrid with drainage layer
     */
    MULTICOMPOSITO: 'multicomposito',

    /**
     * No geogrid selected
     */
    NONE: 'none',

    /**
     * Geocomposite drainage only
     */
    GEOCOMPOSITE_DRAINAGE: 'geocomposite_drainage',

    /**
     * Geogrid only (no drainage)
     */
    GEOGRID_ONLY: 'geogrid_only',
} as const;

/**
 * Drainage Coefficient Constants
 * Based on AASHTO 1993 drainage quality ratings and Excel DRAINAGE_m sheet
 *
 * References:
 * - Excel DRAINAGE_m.txt tables B82:F86 (UNSTABILISED) and B91:F95 (GCD/STABILISED)
 * - Excel DRAINAGE_m.txt (C27, D27, C52, D52)
 * - Excel CALCULATION.txt (G7, G8, H7, H8)
 *
 * AASHTO Table: Recommended m Values
 * | Quality    | <1% wet | 1-5% wet | 2-25% wet | >25% wet |
 * |------------|---------|----------|-----------|----------|
 * | Fair       | 1.15    | 1.05     | 0.8       | 0.8      |
 * | Poor       | 1.05    | 0.8      | 0.6       | 0.6      |
 * | Very poor  | 0.95    | 0.75     | 0.4       | 0.4      |
 *
 * Usage:
 *   import { DRAINAGE_COEFFICIENTS } from '../constants/AASHTOConstants';
 *   const m2 = DRAINAGE_COEFFICIENTS.BASE.FAIR_2_25_PCT; // 0.8
 */
export const DRAINAGE_COEFFICIENTS = {
    /**
     * Base layer drainage coefficients (m2)
     */
    BASE: {
        /**
         * GCD (Geocomposite Drainage) stabilized coefficient
         * Excel: DRAINAGE_m C27 = 1.0 (STABILISED table)
         * Used when drainageBelowBase = "yes"
         */
        GCD_STABILISED: 1.0,

        /**
         * Fair drainage quality, 2-25% wet (UNSTABILISED)
         * Excel: DRAINAGE_m E84 = 0.8 (table B82:F86, row "Fair", column "2-25%")
         * Most common scenario in standard designs
         * Used in DrainageCalculator, AASHTOThicknessOptimizer, MaterialDatabaseService
         */
        FAIR_2_25_PCT: 0.8,

        /**
         * Poor drainage quality, 2-25% wet (UNSTABILISED)
         * Excel: DRAINAGE_m E85 = 0.6 (table B82:F86, row "Poor", column "2-25%")
         * Also used as DEFAULT_UNSTABILISED in CALCULATION.txt G7
         */
        POOR_2_25_PCT: 0.6,

        /**
         * Default unstabilized coefficient
         * Excel: CALCULATION.txt G7 = 0.6 (references Design_LCR!K12)
         * Fallback when drainage quality not specified
         */
        DEFAULT_UNSTABILISED: 0.6,

        /**
         * Very poor drainage quality, 2-25% wet
         * Excel: DRAINAGE_m E86 = 0.4 (table B82:F86, row "Very poor", column "2-25%")
         */
        VERY_POOR: 0.4,
    },

    /**
     * Subbase layer drainage coefficients (m3)
     */
    SUBBASE: {
        /**
         * GCD (Geocomposite Drainage) stabilized coefficient
         * Excel: DRAINAGE_m C52 = 1.15
         * Excel: CALCULATION.txt H8 = 1.15 (when drainageBelowSubbase = "yes")
         * Used when drainageBelowSubbase = "yes"
         */
        GCD_STABILISED: 1.15,

        /**
         * Poor drainage quality, 2-25% wet (GCD/STABILISED)
         * Excel: DRAINAGE_m E94 = 0.8 (table B91:F95, row "Poor", column "2-25%")
         * Common in stabilized subbase designs
         */
        POOR_2_25_PCT_STABILISED: 0.8,

        /**
         * Poor drainage quality, 2-25% wet (UNSTABILISED)
         * Excel: DRAINAGE_m E85 = 0.6 (same as BASE.POOR_2_25_PCT)
         */
        POOR_2_25_PCT: 0.6,

        /**
         * Default unstabilized coefficient
         * Excel: CALCULATION.txt G8 = 0.4 (references Design_LCR!K25)
         * Excel: DRAINAGE_m E86 = 0.4 (Very poor drainage, 2-25% wet)
         * Fallback when drainage quality not specified
         */
        DEFAULT_UNSTABILISED: 0.4,

        /**
         * Fair drainage (legacy compatibility)
         * Excel: DRAINAGE_m E85 = 0.6
         */
        FAIR: 0.6,
    },

    /**
     * Default fallback when no drainage data available
     */
    DEFAULT: 1.0,
} as const;

/**
 * Default Material Properties for Fallback Calculations
 *
 * These values represent standard material properties used as fallbacks
 * when specific material data is unavailable in calculations.
 *
 * References:
 * - Excel DATABASE.txt (rows 498, 546)
 * - Used in PavedCalculationOrchestrator.ts drainage calculations
 *
 * Note: Permeability values are in m/day (converted from Excel m/s × 86400)
 *
 * Usage:
 *   import { DEFAULT_MATERIAL_PROPERTIES } from '../constants/AASHTOConstants';
 *   const porosity = baseMaterial?.effectivePorosity || DEFAULT_MATERIAL_PROPERTIES.BASE.EFFECTIVE_POROSITY;
 */
export const DEFAULT_MATERIAL_PROPERTIES = {
    /**
     * Base layer default properties
     * Material: "Mixed granular soil 1#" (Excel DATABASE.txt C91, row 498)
     */
    BASE: {
        /**
         * Effective porosity for base course
         * Excel: DATABASE.txt H91 = 0.32
         * Material: Mixed granular soil 1# (well graded sand, CBR 70)
         */
        EFFECTIVE_POROSITY: 0.32,

        /**
         * Permeability coefficient for base course (m/day)
         * Excel: DATABASE.txt I91 = 0.0001 m/s = 8.64 m/day ≈ 8.46 m/day
         * Material: Mixed granular soil 1#
         * Used in drainage time calculations
         */
        PERMEABILITY_COEFF: 8.46,
    },

    /**
     * Subbase layer default properties
     * Material: "Naturally stabilized mix 2#" (Excel DATABASE.txt C98, row 546)
     */
    SUBBASE: {
        /**
         * Effective porosity for subbase course
         * Excel: DATABASE.txt H98 = 0.2
         * Material: Naturally stabilized mix 2# (CBR 10)
         */
        EFFECTIVE_POROSITY: 0.2,

        /**
         * Permeability coefficient for subbase course (m/day)
         * Excel: DATABASE.txt I98 = 1e-06 m/s = 0.0864 m/day ≈ 0.0846 m/day
         * Material: Naturally stabilized mix 2#
         * Used in drainage time calculations
         */
        PERMEABILITY_COEFF: 0.0846,
    },
} as const;

/**
 * Type definitions for better TypeScript support
 */
export type UnitConversionKey = keyof typeof UNIT_CONVERSION;
export type DefaultLCRKey = keyof typeof DEFAULT_LCR;
export type GeogridProductKey = keyof typeof GEOGRID_PRODUCTS;
export type DrainageCoefficientKey = keyof typeof DRAINAGE_COEFFICIENTS;
export type DefaultMaterialPropertiesKey = keyof typeof DEFAULT_MATERIAL_PROPERTIES;
